import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Sparkles, ChevronDown, ChevronUp, Save, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";

/**
 * Collapsible SEO metadata editor for use inside every admin content
 * editor (News, Players, Chants, Fan Clubs, Fan Memories, Transfer News).
 *
 * Fetches the current seo_title / seo_description / seo_keywords /
 * image_alt for the given doc, shows the four fields as editable inputs,
 * and offers "Regenerate with AI" (LLM re-derives from current H1+body)
 * and "Save" (persist the human-edited values).
 *
 * Props:
 *   collection  — DB collection name ("players" | "news" | "chants" | ...)
 *   docId       — the doc's `id` (uuid)
 *   compact     — when true, renders a slim variant that fits inside a Dialog footer
 */
export default function SeoMetaEditor({ collection, docId, compact = false }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(!compact);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [meta, setMeta] = useState({
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    image_alt: "",
  });
  const [markers, setMarkers] = useState({
    seo_meta_generated_at: null,
    seo_meta_invalidated_at: null,
  });

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchMeta = async () => {
    if (!collection || !docId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/seo/meta/${collection}/${docId}`, { headers: authHeaders });
      setMeta({
        seo_title: res.data.seo_title || "",
        seo_description: res.data.seo_description || "",
        seo_keywords: res.data.seo_keywords || "",
        image_alt: res.data.image_alt || "",
      });
      setMarkers({
        seo_meta_generated_at: res.data.seo_meta_generated_at,
        seo_meta_invalidated_at: res.data.seo_meta_invalidated_at,
      });
    } catch (e) {
      // 404 just means the doc was just created and hasn't been indexed
      // yet — silently start with empty fields.
      if (e.response?.status !== 404) {
        toast.error(`Load SEO meta failed: ${e.response?.data?.detail || e.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchMeta();
  }, [open, collection, docId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/seo/regenerate-meta/${collection}/${docId}`,
        {},
        { headers: authHeaders }
      );
      setMeta({
        seo_title: res.data.seo_title || "",
        seo_description: res.data.seo_description || "",
        seo_keywords: res.data.seo_keywords || "",
        image_alt: res.data.image_alt || "",
      });
      toast.success("AI regenerated all 4 fields — review and Save if you want to keep them.");
    } catch (e) {
      toast.error(`Regenerate failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo/meta/${collection}/${docId}`, meta, { headers: authHeaders });
      toast.success("SEO metadata saved");
      // Refresh markers so the "stale" banner clears
      fetchMeta();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const isStale = markers.seo_meta_invalidated_at && !meta.seo_title;

  // Character counters — soft targets driven by the LLM prompt spec.
  const titleLen = meta.seo_title.length;
  const descLen = meta.seo_description.length;
  const altWordCount = meta.image_alt.trim() ? meta.image_alt.trim().split(/\s+/).length : 0;
  const titleWarn = titleLen > 0 && (titleLen < 30 || titleLen > 70);
  const descWarn = descLen > 0 && (descLen < 110 || descLen > 170);
  const altWarn = altWordCount > 0 && (altWordCount < 8 || altWordCount > 25);

  return (
    <div className="border rounded-lg bg-slate-50/60" data-testid={`seo-meta-editor-${collection}-${docId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100/60 rounded-t-lg"
        data-testid={`seo-meta-toggle-${collection}-${docId}`}
      >
        <span className="flex items-center gap-2 font-semibold text-sm text-slate-700">
          <Sparkles className="w-4 h-4 text-violet-600" />
          SEO metadata
          {isStale ? (
            <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> stale — regenerate
            </span>
          ) : meta.seo_title ? (
            <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> set
            </span>
          ) : (
            <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
              empty
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>

      {open && (
        <div className="p-4 pt-0 space-y-3">
          {loading ? (
            <p className="text-sm text-slate-500 py-2">Loading…</p>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase text-slate-500">SEO title</Label>
                  <span className={`text-xs ${titleWarn ? "text-amber-700" : "text-slate-400"}`}>
                    {titleLen}/60 chars
                  </span>
                </div>
                <Input
                  value={meta.seo_title}
                  onChange={(e) => setMeta((m) => ({ ...m, seo_title: e.target.value }))}
                  placeholder="e.g. Bobby Moore — West Ham United Legend Defender | All WHUFC"
                  className="mt-1 bg-white"
                  data-testid={`seo-meta-title-${collection}-${docId}`}
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Meta description</Label>
                  <span className={`text-xs ${descWarn ? "text-amber-700" : "text-slate-400"}`}>
                    {descLen}/160 chars
                  </span>
                </div>
                <Textarea
                  value={meta.seo_description}
                  onChange={(e) => setMeta((m) => ({ ...m, seo_description: e.target.value }))}
                  placeholder="140-160 chars, benefit-led. Include one page-specific entity + West Ham anchor."
                  rows={2}
                  className="mt-1 bg-white text-sm"
                  data-testid={`seo-meta-desc-${collection}-${docId}`}
                />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">
                  Keywords <span className="font-normal normal-case text-slate-400">(comma-separated, 6-8 terms, must include &quot;West Ham United&quot;)</span>
                </Label>
                <Input
                  value={meta.seo_keywords}
                  onChange={(e) => setMeta((m) => ({ ...m, seo_keywords: e.target.value }))}
                  placeholder="West Ham United, Bobby Moore, defender, 1966 World Cup, Hammers legend, England captain"
                  className="mt-1 bg-white font-mono text-xs"
                  data-testid={`seo-meta-keywords-${collection}-${docId}`}
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase text-slate-500">Image alt text</Label>
                  <span className={`text-xs ${altWarn ? "text-amber-700" : "text-slate-400"}`}>
                    {altWordCount}/18 words
                  </span>
                </div>
                <Input
                  value={meta.image_alt}
                  onChange={(e) => setMeta((m) => ({ ...m, image_alt: e.target.value }))}
                  placeholder="Bobby Moore lifting the FA Cup at Wembley in claret and blue West Ham United kit."
                  className="mt-1 bg-white text-sm"
                  data-testid={`seo-meta-alt-${collection}-${docId}`}
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="text-xs text-slate-500">
                  {markers.seo_meta_generated_at && (
                    <>Last set {new Date(markers.seo_meta_generated_at).toLocaleString()}</>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleRegenerate}
                    disabled={regenerating || saving}
                    variant="outline"
                    size="sm"
                    className="border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid={`seo-meta-regen-${collection}-${docId}`}
                  >
                    {regenerating ? (
                      <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Generating…</>
                    ) : (
                      <><Sparkles className="w-3 h-3 mr-1" /> Regenerate with AI</>
                    )}
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving || regenerating}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`seo-meta-save-${collection}-${docId}`}
                  >
                    {saving ? (
                      <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Saving…</>
                    ) : (
                      <><Save className="w-3 h-3 mr-1" /> Save</>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
